import dayjs from 'dayjs';
import { t } from 'i18next';

import { screenType } from '../constants/enum';

import type { NegotiationType } from '../api/firebase/firestore/negotiation/negotiationType';
import type { ContactType } from '../components/pages/contact/ContactConfirm';
import type {
  DocumentData,
  DocumentSnapshot,
  Timestamp
} from 'firebase/firestore';

const slack = (message: string): void => {
  const url = process.env.REACT_APP_SLACK_WEBHOOK_URL ?? '';
  const data = {
    text: message
  };
  const xml = new XMLHttpRequest();
  xml.open('POST', url, false);
  xml.setRequestHeader(
    'content-type',
    'application/x-www-form-urlencoded;charset=UTF-8'
  );
  xml.send(`payload=${JSON.stringify(data)}`);
};

const retoolUrlEnv = (env: string): string => {
  if (env === 'stg') {
    return '&_environment=staging';
  } else if (env === 'prd') {
    return '&_environment=production';
  } else if (env === 'dev') {
    return '&_environment=dev';
  }
  return '';
};

export const sendSlackAfterOrderApproval = (
  screen: number,
  order: DocumentData,
  negotiation: NegotiationType,
  publisherName: string,
  issueName: string,
  clientName: string,
  agentName: string
): void => {
  const envUrl = retoolUrlEnv(process.env.REACT_APP_ENV ?? '');
  const compensation = getCompensationsString(order.compensation as number[]);
  const urlLinkTemplate = (url: string, text: string): string =>
    `<${encodeURIComponent(url)}|${text}>`;
  slack(`${
    screen === screenType.client
      ? `広告主「${clientName}」`
      : `代理店「${agentName}」`
  }の与件「${issueName}」について、媒体社「${publisherName}」への発注が承認されました。\n
発注内容
【発注主名】${order.companyName as string}
【受注広告商品/パッケージ名】${order.pkgName as string}
【受注金額】${order.amount as number}
【掲載期間】${dayjs((order.publishStartDate as Timestamp)?.toDate()).format(
    'YYYY/MM/DD'
  )}〜${dayjs((order.publishEndDate as Timestamp)?.toDate()).format(
    'YYYY/MM/DD'
  )}
【握ってるKPI】${order.kpi as string}
【二次利用やデータ利用などオプションの有無】${
    String(order.optionFlg) === '1' ? 'あり' : 'なし'
  }
【オプションの内容】${order.optionContent as string}}
【レポートの有無】${String(order.reportFlg) === '1' ? 'あり' : 'なし'}
【レポートの提出予定日】${
    order.reportSubmissionDate
      ? dayjs((order.reportSubmissionDate as Timestamp).toDate()).format(
          'YYYY/MM/DD'
        )
      : 'なし'
  }
【特記事項】${compensation}

メディアへの支払い方法
【振込タイミング】${String(order.transferTiming) === '1' ? '前払い' : '後払い'}
【振込期日】${order.depositTiming as string}

${
  screen === screenType.client
    ? `広告主URL：${urlLinkTemplate(
        `https://mediage.retool.com/apps/2f6bde98-2340-11ed-bd0a-1771b272afbd/%5BMediAge%5DR02_R03_EditDetail_Client?clientId=${negotiation.clientId}${envUrl}`,
        '広告主のURL'
      )}`
    : `代理店URL：${urlLinkTemplate(
        `https://mediage.retool.com/apps/043a0ffa-4394-11ed-b8d2-df711305a588/%5BMediAge%5DR09_R10_EditDetail_Agent?=&agentId=${negotiation.agentId}${envUrl}`,
        '代理店のURL'
      )}`
}
与件URL：${urlLinkTemplate(
    `https://mediage.retool.com/apps/76c0ec6c-3a3c-11ed-b271-97093ce557fd/%5BMediAge%5DR19_EditDetail_Issue?issueId=${negotiation.issueId}${envUrl}`,
    '与件のURL'
  )}
媒体社URL：${urlLinkTemplate(
    `https://mediage.retool.com/apps/63038b38-3df9-11ed-a039-4f461572c243/%5BMediAge%5DR12_R13_EditDetail_Publisher?publisherId=${negotiation.publisherId}${envUrl}`,
    '媒体社のURL'
  )}
メディアURL：${urlLinkTemplate(
    `https://mediage.retool.com/apps/e7f32662-4aad-11ed-b856-035bfad2961c/%5BMediAge%5DR14_R16_EditDetail_Media?mediaId=${negotiation.mediaId}${envUrl}`,
    'メディアのURL'
  )}`);
};

const getCompensationsString = (compensation: number[]): string =>
  compensation
    .map((elm) => {
      if (elm === 1) {
        return t('message.order.compensation.1');
      } else if (elm === 2) {
        return t('message.order.compensation.2');
      } else if (elm === 3) {
        return t('message.order.compensation.3');
      } else if (elm === 4) {
        return t('message.order.compensation.4');
      } else if (elm === 5) {
        return t('message.order.compensation.5');
      } else if (elm === 6) {
        return t('message.order.compensation.6');
      }
      return '';
    })
    .join(',');

export const sendSlackRegisterAll = (
  companySnap: DocumentSnapshot<DocumentData>,
  userSnap: DocumentSnapshot<DocumentData>,
  industry: string
): void => {
  if (companySnap.exists() && userSnap.exists()) {
    let department, screen, url;
    const envUrl = retoolUrlEnv(process.env.REACT_APP_ENV ?? '');

    if (userSnap.data()?.type === screenType.client) {
      screen = '広告主';
      department = `部署名：${(userSnap.data()?.department as string) ?? ''}\n`;
      url = `https://mediage.retool.com/apps/2f6bde98-2340-11ed-bd0a-1771b272afbd/%5BMediAge%5DR02_R03_EditDetail_Client?clientId=${
        companySnap.data()?.companyId as string
      }${envUrl}`;
    } else if (userSnap.data()?.type === screenType.agent) {
      screen = '代理店';
      department = `部署名：${(userSnap.data()?.department as string) ?? ''}\n`;
      url = `https://mediage.retool.com/apps/043a0ffa-4394-11ed-b8d2-df711305a588/%5BMediAge%5DR09_R10_EditDetail_Agent?=&agentId=${
        companySnap.data()?.companyId as string
      }${envUrl}`;
    } else {
      screen = '媒体社';
      department = '';
      url = `https://mediage.retool.com/apps/63038b38-3df9-11ed-a039-4f461572c243/%5BMediAge%5DR12_R13_EditDetail_Publisher?publisherId=${
        companySnap.data()?.companyId as string
      }${envUrl}`;
    }

    let retoolUrl = encodeURI(url);
    retoolUrl = retoolUrl.replace(/[=]/g, '%3D');
    retoolUrl = retoolUrl.replace(/&/g, '%26');

    slack(`${screen}が会員登録しました。\n
登録内容を確認して、承認作業をしてください。\n
[登録内容]\n
企業名：${(companySnap.data()?.name as string) ?? ''}\n
自社URL：${(companySnap.data()?.url as string) ?? ''}\n
業種：${industry}\n
担当者名：${(userSnap.data()?.name as string) ?? ''}\n
${department}
担当者メールアドレス：${(userSnap.data()?.email as string) ?? ''}\n
管理画面：${retoolUrl}`);
  }
};

export const sendSlackRegisterPublisher = (
  companySnap: DocumentSnapshot<DocumentData>,
  userSnap: DocumentSnapshot<DocumentData>,
  industry: string
): void => {
  let retoolUrl = `https://mediage.retool.com/apps/63038b38-3df9-11ed-a039-4f461572c243/%5BMediAge%5DR12_R13_EditDetail_Publisher?publisherId=${
    companySnap.data()?.companyId as string
  }${retoolUrlEnv(process.env.REACT_APP_ENV ?? '')}`;
  retoolUrl = encodeURI(retoolUrl);
  retoolUrl = retoolUrl.replace(/[=]/g, '%3D');
  retoolUrl = retoolUrl.replace(/&/g, '%26');

  if (companySnap.exists() && userSnap.exists()) {
    slack(`媒体社が会員登録しました。\n
登録内容を確認して、承認作業をしてください。\n
[登録内容]\n
企業名：${(companySnap.data()?.name as string) ?? ''}\n
自社URL：${(companySnap.data()?.url as string) ?? ''}\n
業種：${industry}\n
担当者名：${(userSnap.data()?.name as string) ?? ''}\n
担当者メールアドレス：${(userSnap.data()?.email as string) ?? ''}\n
管理画面：${retoolUrl}`);
  }
};

export const sendSlackIssueClient = (
  companySnap: DocumentSnapshot<DocumentData>,
  issueSnap: DocumentSnapshot<DocumentData>,
  typeText: string,
  genre: string,
  purpose: string,
  budget: string,
  agentName: string
): void => {
  if (companySnap.exists() && issueSnap.exists()) {
    let retoolUrl = `https://mediage.retool.com/apps/880a3ede-2442-11ed-950b-53d24c069a9e/%5BMediAge%5DR04_R06_EditDetail_Issue?agentId=${
      issueSnap.data()?.agentId as string
    }&agentName=${encodeURI(agentName)}&clientId=${
      companySnap.data()?.companyId as string
    }&clientName=${encodeURI(companySnap.data()?.name as string)}&issueId=${
      issueSnap.data()?.issueId as string
    }&issueName=${encodeURI(issueSnap.data()?.name as string)}${retoolUrlEnv(
      process.env.REACT_APP_ENV ?? ''
    )}#=`;
    retoolUrl = encodeURI(retoolUrl);
    retoolUrl = retoolUrl.replace(/[=]/g, '%3D');
    retoolUrl = retoolUrl.replace(/&/g, '%26');

    const budgetText = budget !== '' ? budget : t('consult.label1');

    slack(`広告主「${
      (companySnap.data()?.name as string) ?? ''
    }」が${typeText}しました\n
内容を確認してください。\n
管理画面：${retoolUrl}\n
[登録内容]\n
与件タイトル：${(issueSnap.data()?.name as string) ?? ''}\n
商材ジャンル：${genre}\n
商材タイプ：${
      (issueSnap.data()?.merchandiseType as boolean) ? '新規' : '既存'
    }\n
出稿の目的：${purpose}\n
実施時期：${
      issueSnap.data()?.startDate
        ? dayjs((issueSnap.data()?.startDate as Timestamp).toDate()).format(
            'YYYY/MM/DD'
          )
        : t('consult.label1')
    }\n
募集期間：${
      (
        issueSnap.data()?.applicationDate as {
          start: Timestamp;
          end: Timestamp;
        }
      ).start
        ? dayjs(
            (
              issueSnap.data()?.applicationDate as {
                start: Timestamp;
                end: Timestamp;
              }
            ).start.toDate()
          ).format('YYYY/MM/DD')
        : ''
    }〜${
      (
        issueSnap.data()?.applicationDate as {
          start: Timestamp;
          end: Timestamp;
        }
      ).end
        ? dayjs(
            (
              issueSnap.data()?.applicationDate as {
                start: Timestamp;
                end: Timestamp;
              }
            ).end.toDate()
          ).format('YYYY/MM/DD')
        : ''
    }\n
予算規模：${budgetText}\n
    `);
  }
};

export const sendSlackIssueAgent = (
  companySnap: DocumentSnapshot<DocumentData>,
  issueSnap: DocumentSnapshot<DocumentData>,
  typeText: string,
  genre: string,
  purpose: string,
  budget: string
): void => {
  if (companySnap.exists() && issueSnap.exists()) {
    let retoolUrl = `https://mediage.retool.com/apps/043a0ffa-4394-11ed-b8d2-df711305a588/%5BMediAge%5DR09_R10_EditDetail_Agent?=&agentId=${
      issueSnap.data()?.agentId as string
    }&_releaseVersion=latest${retoolUrlEnv(process.env.REACT_APP_ENV ?? '')}`;

    retoolUrl = encodeURI(retoolUrl);
    retoolUrl = retoolUrl.replace(/[=]/g, '%3D');
    retoolUrl = retoolUrl.replace(/&/g, '%26');

    const budgetText = budget !== '' ? budget : t('consult.label1');

    slack(`代理店「${
      (companySnap.data()?.name as string) ?? ''
    }」が${typeText}しました\n
内容を確認してください。\n
管理画面：${retoolUrl}\n
[登録内容]\n
与件タイトル：${(issueSnap.data()?.name as string) ?? ''}\n
商材ジャンル：${genre}\n
商材タイプ：${
      (issueSnap.data()?.merchandiseType as boolean) ? '新規' : '既存'
    }\n
出稿の目的：${purpose}\n
実施時期：${
      issueSnap.data()?.startDate
        ? dayjs((issueSnap.data()?.startDate as Timestamp).toDate()).format(
            'YYYY/MM/DD'
          )
        : t('consult.label1')
    }\n
募集期間：${
      (
        issueSnap.data()?.applicationDate as {
          start: Timestamp;
          end: Timestamp;
        }
      ).start
        ? dayjs(
            (
              issueSnap.data()?.applicationDate as {
                start: Timestamp;
                end: Timestamp;
              }
            ).start.toDate()
          ).format('YYYY/MM/DD')
        : ''
    }〜${
      (
        issueSnap.data()?.applicationDate as {
          start: Timestamp;
          end: Timestamp;
        }
      ).end
        ? dayjs(
            (
              issueSnap.data()?.applicationDate as {
                start: Timestamp;
                end: Timestamp;
              }
            ).end.toDate()
          ).format('YYYY/MM/DD')
        : ''
    }\n
予算規模：${budgetText}\n
    `);
  }
};

export const sendSlackMediaPublisher = (
  companySnap: DocumentSnapshot<DocumentData>,
  mediaSnap: DocumentSnapshot<DocumentData>,
  genre: string,
  mediaType: string
): void => {
  if (companySnap.exists() && mediaSnap.exists()) {
    let retoolUrl = `https://mediage.retool.com/apps/e7f32662-4aad-11ed-b856-035bfad2961c/%5BMediAge%5DR14_R16_EditDetail_Media?=&mediaId=${
      mediaSnap.data()?.mediaId as string
    }&mediaName=${encodeURI(mediaSnap.data()?.name as string)}&publisherId=${
      companySnap.data()?.companyId as string
    }&publisherName=${encodeURI(
      companySnap.data()?.name as string
    )}&_releaseVersion=latest${retoolUrlEnv(process.env.REACT_APP_ENV ?? '')}`;

    retoolUrl = encodeURI(retoolUrl);
    retoolUrl = retoolUrl.replace(/[=]/g, '%3D');
    retoolUrl = retoolUrl.replace(/&/g, '%26');

    slack(`媒体社「${
      (companySnap.data()?.name as string) ?? ''
    }」がメディア登録しました\n
内容を確認してください。\n
管理画面：${retoolUrl}\n
[登録内容]\n
メディアタイトル：${(mediaSnap.data()?.name as string) ?? ''}\n
ジャンル：${genre}\n
メディア種別：${mediaType}\n
    `);
  }
};

export const sendSlackContact = (
  locationInfo: ContactType,
  category: string
): void => {
  let phone;
  if (locationInfo.phone === 'undefined') {
    phone = 'なし';
  } else {
    phone = locationInfo.phone;
  }

  slack(`お問い合わせがありました。\n
    --------------------------------------------\n
    【会社名】\n
    ${locationInfo.companyName}\n
    【メールアドレス】\n
    ${locationInfo.email}\n
    【電話番号】\n
    ${phone}\n
    【お問い合わせの種類】\n
    ${category}\n
    【お問い合わせ内容】\n
    ${locationInfo.message}\n
    --------------------------------------------`);
};
